import { createContext, useCallback, useContext, useEffect, useRef, useState, useSyncExternalStore, type CSSProperties } from 'react';
import { Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AssinaturaMalgo } from '../componentes/assinatura';
import { duracaoDaFase, faseAoNavegar, faseInicial, fotoPermitida, proximaFase, splashDaFase, tempos, type Fase, type Splash } from './abertura';
import farolAvif768 from '../estilos/login/farol-768.avif';
import farolAvif1280 from '../estilos/login/farol-1280.avif';
import farolAvif1920 from '../estilos/login/farol-1920.avif';
import farolWebp768 from '../estilos/login/farol-768.webp';
import farolWebp1280 from '../estilos/login/farol-1280.webp';
import farolWebp1920 from '../estilos/login/farol-1920.webp';
import { srcSetFundo, TEXTOS_PADRAO, urlFundo, urlLogo, useTelaEntrada, type ImagemTelaEntrada, type TelaEntrada, type TextosTelaEntrada } from '../nucleo/tela-entrada';

/**
 * Fase 2 (W32): fundo das páginas públicas (login, segundo passo, recuperar e redefinir senha, convite e saída). Fica montado
 * enquanto a pessoa anda entre elas, então a foto não recarrega e a abertura toca uma vez só. Camadas: cor sólida de reserva,
 * a foto do farol em preto e branco e, por cima, a mesma foto colorida recortada por um círculo que abre ou fecha conforme a
 * fase. O splash (logo, nome e barra) cobre a tela na abertura, no pós-login e no "Saindo". Regras e tempos em `abertura.ts`.
 *
 * Tela de entrada (30/10): a logo e o fundo podem ser trocados pela plataforma (Admin > Painel admin > Tela de entrada). A
 * configuração vem da rota pública (`nucleo/tela-entrada.ts`); sem ela, com a rota fora ou com a imagem falhando, voltam o
 * farol e a logo da Malgo. Enquanto a configuração carrega (até 2,5 s), fica só a cor sólida: o farol não pisca antes.
 */

interface ValorFundo {
  fase: Fase;
  /** Pós-login: fecha a cor e mostra o splash enquanto `tarefa` roda; resolve quando as duas coisas terminam. */
  entrar(tarefa: () => Promise<unknown>): Promise<void>;
  /** Tela de entrada (30/10): a logo configurada pela plataforma (null = a da Malgo). */
  logo: ImagemTelaEntrada | null;
  /** Lista de 30/10 (item 1): textos da faixa da marca configurados pela plataforma (null = o padrão do idioma). */
  textos: TextosTelaEntrada;
}

const Ctx = createContext<ValorFundo>({ fase: 'pronto', entrar: async (tarefa) => void (await tarefa()), logo: null, textos: TEXTOS_PADRAO });
export const useFundoPublico = () => useContext(Ctx);

/** A abertura toca na primeira vez que o login aparece neste carregamento da página. */
let aberturaTocou = false;

const consultaReduzido = '(prefers-reduced-motion: reduce)';
function assinarReduzido(aviso: () => void) {
  const m = window.matchMedia?.(consultaReduzido);
  m?.addEventListener('change', aviso);
  return () => m?.removeEventListener('change', aviso);
}
export function useMovimentoReduzido() {
  return useSyncExternalStore(
    assinarReduzido,
    () => !!window.matchMedia?.(consultaReduzido).matches,
    () => false,
  );
}

function conexaoDoNavegador() {
  return (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
}

const esperar = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export function FundoPublico() {
  const { pathname } = useLocation();
  const tela = useTelaEntrada();
  const reduzido = useMovimentoReduzido();
  const [estado, setEstado] = useState(() => ({ fase: faseInicial(pathname, reduzido, aberturaTocou), anterior: null as Fase | null, pulou: false }));
  const ir = useCallback((fase: Fase, pulou = false) => setEstado((e) => (e.fase === fase ? e : { fase, anterior: e.fase, pulou })), []);
  useEffect(() => {
    aberturaTocou = true;
  }, []);

  // Troca de página pública (saída e, depois da despedida, o login de novo).
  const [caminho, setCaminho] = useState(pathname);
  if (caminho !== pathname) {
    setCaminho(pathname);
    const nova = faseAoNavegar(pathname, estado.fase);
    if (nova !== estado.fase) setEstado({ fase: nova, anterior: estado.fase, pulou: false });
  }

  const t = tempos(reduzido);
  useEffect(() => {
    const d = duracaoDaFase(estado.fase, t);
    const proxima = proximaFase(estado.fase);
    if (d === null || !proxima) return;
    const id = setTimeout(() => ir(proxima), estado.pulou && estado.fase === 'revelando' ? Math.max(t.cartao, t.sumir) : d);
    return () => clearTimeout(id);
  }, [estado.fase, estado.pulou, t, ir]);

  // Quem tem pressa: tecla, clique ou toque durante o splash da abertura vai direto ao formulário.
  useEffect(() => {
    if (estado.fase !== 'carregando') return;
    const pular = () => ir('revelando', true);
    window.addEventListener('keydown', pular);
    window.addEventListener('pointerdown', pular);
    return () => {
      window.removeEventListener('keydown', pular);
      window.removeEventListener('pointerdown', pular);
    };
  }, [estado.fase, ir]);

  const reduzidoRef = useRef(reduzido);
  reduzidoRef.current = reduzido;
  const entrar = useCallback(
    async (tarefa: () => Promise<unknown>) => {
      ir('fechando');
      await Promise.all([tarefa().catch(() => undefined), esperar(tempos(reduzidoRef.current).posLogin)]);
    },
    [ir],
  );

  const estilo = {
    '--login-revelar': `${t.revelar}ms`,
    '--login-fechar': `${t.fechar}ms`,
    '--login-cartao': `${t.cartao}ms`,
    '--login-atraso': `${estado.pulou ? 0 : t.atrasoCartao}ms`,
    '--login-sumir': `${t.sumir}ms`,
  } as CSSProperties;
  const splash = splashDaFase(estado.fase, estado.anterior);

  return (
    <Ctx.Provider value={{ fase: estado.fase, entrar, logo: tela?.logo ?? null, textos: tela?.textos ?? TEXTOS_PADRAO }}>
      <div className="relative min-h-dvh" style={estilo} data-fundo-publico={estado.fase}>
        <FotoDoFarol fase={estado.fase} tela={tela} />
        <div className="relative">
          <Outlet />
        </div>
        {splash && <SplashMarca splash={splash} logo={tela?.logo ?? null} />}
      </div>
    </Ctx.Provider>
  );
}

interface FontesFoto {
  chave: string;
  avif: string;
  webp: string;
  webpPadrao: string;
}
const FONTES_FAROL: FontesFoto = {
  chave: 'farol',
  avif: `${farolAvif768} 768w, ${farolAvif1280} 1280w, ${farolAvif1920} 1920w`,
  webp: `${farolWebp768} 768w, ${farolWebp1280} 1280w, ${farolWebp1920} 1920w`,
  webpPadrao: farolWebp1280,
};
const fontesDaVersao = (versao: string): FontesFoto => ({ chave: versao, avif: srcSetFundo(versao, 'avif'), webp: srcSetFundo(versao, 'webp'), webpPadrao: urlFundo(versao, 1280, 'webp') });

/**
 * Foto de fundo em AVIF ou WebP no tamanho da tela: a configurada pela plataforma ou o farol. A configurada que falha (rota
 * fora, arquivo removido) cai no farol; sem foto nenhuma (conexão lenta, Save-Data ou erro), a cor sólida de reserva.
 */
function FotoDoFarol({ fase, tela }: { fase: Fase; tela: TelaEntrada | undefined }) {
  const [permitida] = useState(() => fotoPermitida(conexaoDoNavegador()));
  const [falhouVersao, setFalhouVersao] = useState<string | null>(null);
  const [visivel, setVisivel] = useState(false);
  const versao = tela?.fundo?.versao ?? null;
  const personalizada = !!versao && falhouVersao !== versao;
  const fontes = personalizada ? fontesDaVersao(versao) : FONTES_FAROL;
  const pronta = tela !== undefined;
  return (
    <div
      aria-hidden
      className="login-fundo fixed inset-0 overflow-hidden"
      data-foto={permitida ? (visivel ? 'carregada' : 'carregando') : 'pulada'}
      data-fundo={pronta ? (personalizada ? 'personalizado' : 'padrao') : undefined}
    >
      {permitida && pronta && <Camadas key={fontes.chave} fase={fase} fontes={fontes} personalizada={personalizada} aoMudar={setVisivel} aoFalhar={personalizada ? () => setFalhouVersao(versao) : undefined} />}
    </div>
  );
}

function Camadas({ fase, fontes, personalizada, aoMudar, aoFalhar }: { fase: Fase; fontes: FontesFoto; personalizada: boolean; aoMudar: (v: boolean) => void; aoFalhar?: () => void }) {
  const [carregada, setCarregada] = useState(false);
  const [falhou, setFalhou] = useState(false);
  const ref = useRef<HTMLImageElement>(null);
  // Foto já em cache: o onLoad pode ter disparado antes do React ligar o evento.
  useEffect(() => {
    if (ref.current?.complete && ref.current.naturalWidth > 0) setCarregada(true);
  }, []);
  const visivel = carregada && !falhou;
  useEffect(() => aoMudar(visivel), [visivel, aoMudar]);
  const foto = (classe: string, principal: boolean) => (
    <picture>
      <source type="image/avif" srcSet={fontes.avif} sizes="100vw" />
      <img
        ref={principal ? ref : undefined}
        src={fontes.webpPadrao}
        srcSet={fontes.webp}
        sizes="100vw"
        alt=""
        decoding="async"
        fetchPriority={principal ? 'high' : undefined}
        onLoad={principal ? () => setCarregada(true) : undefined}
        onError={
          principal
            ? () => {
                setFalhou(true);
                aoFalhar?.();
              }
            : undefined
        }
        data-fase={principal ? undefined : fase}
        data-personalizada={personalizada || undefined}
        className={`login-foto transition-opacity duration-500 ${classe} ${visivel ? 'opacity-100' : 'opacity-0'}`}
      />
    </picture>
  );
  return (
    <>
      {foto('login-foto-pb', true)}
      {foto('login-cor', false)}
    </>
  );
}

/**
 * Tela de entrada (30/10): a logo configurada (PNG ou WebP com transparência, até 200 px de largura exibida) no lugar do
 * símbolo e do nome; se a imagem falhar, voltam o símbolo e o nome da Malgo. `grande` é a do splash. Rodada rlogin-1 (VIS-M1):
 * sobre a placa `login-placa-logo`, ligada pelos tokens só nos estilos de faixa da marca clara (no tema claro).
 */
export function LogoDaEntrada({ logo, grande = false, nome = true }: { logo: ImagemTelaEntrada | null; grande?: boolean; nome?: boolean }) {
  const [falhou, setFalhou] = useState<string | null>(null);
  if (logo && falhou !== logo.versao) {
    return (
      <picture data-logo-entrada="personalizada" className="login-placa-logo">
        <source type="image/webp" srcSet={urlLogo(logo.versao, 'webp')} />
        <img src={urlLogo(logo.versao, 'png')} alt="MalgoOS" decoding="async" onError={() => setFalhou(logo.versao)} className={`block w-auto object-contain ${grande ? 'max-h-20 max-w-60' : 'max-h-9 max-w-50 lg:max-h-10'}`} />
      </picture>
    );
  }
  return (
    <span data-logo-entrada="padrao" className="contents">
      <span className="text-tinta-1">
        <AssinaturaMalgo className={grande ? 'h-14 max-w-full sm:h-20' : 'h-8 lg:h-9'} />
      </span>
      {nome && <span className="sr-only">MalgoOS</span>}
    </span>
  );
}

function SplashMarca({ splash, logo }: { splash: NonNullable<Splash>; logo: ImagemTelaEntrada | null }) {
  const { t } = useTranslation();
  const rotulo = splash.tipo === 'saindo' ? t('saida.saindo') : splash.tipo === 'entrando' ? t('login.entrando') : t('login.carregando');
  return (
    <div
      className="login-splash superficie-marca"
      data-splash={splash.tipo}
      data-sumindo={splash.sumindo || undefined}
      role={splash.sumindo ? undefined : 'status'}
      aria-hidden={splash.sumindo || undefined}
      aria-label={rotulo}
    >
      <div className="login-splash-logo">
        <LogoDaEntrada logo={logo} grande nome={false} />
      </div>
      <p className="login-splash-nome text-2xl font-semibold tracking-tight text-tinta-1">{splash.tipo === 'saindo' ? t('saida.saindo') : 'MalgoOS'}</p>
      <div className="login-barra" aria-hidden>
        <span />
      </div>
    </div>
  );
}
